import React, { useState } from 'react';
import { X, MapPin, Users, ArrowRight } from 'lucide-react';
import { darkModeClasses } from '../../utils/darkMode';
import { Town } from '../../types';

interface TownSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  towns: Town[];
  onSelectTown: (townId: string) => void;
  loading?: boolean;
}

export const TownSelectionModal: React.FC<TownSelectionModalProps> = ({
  isOpen,
  onClose,
  towns,
  onSelectTown,
  loading = false
}) => {
  const [selectedTownId, setSelectedTownId] = useState<string>('');

  const handleSubmit = () => {
    if (selectedTownId) {
      onSelectTown(selectedTownId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
            <MapPin className="h-6 w-6 mr-2 text-blue-600" />
            Select Town for Member Portal
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <p className={`text-sm ${darkModeClasses.textSecondary} mb-4`}>
              Choose which town's member portal you want to access. You'll be able to view the portal from a member's perspective.
            </p>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {towns.map((town) => (
              <div
                key={town.id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedTownId === town.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-dark-border-primary hover:border-blue-300 dark:hover:border-blue-600'
                }`}
                onClick={() => setSelectedTownId(town.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className={`font-semibold ${darkModeClasses.textPrimary}`}>
                      {town.name}
                    </h3>
                    <p className={`text-sm ${darkModeClasses.textSecondary} mt-1`}>
                      {town.location}
                    </p>
                    <div className="flex items-center space-x-4 mt-2 text-xs">
                      <div className="flex items-center space-x-1">
                        <Users className="h-3 w-3" />
                        <span>{town.memberCount || 0} members</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className={darkModeClasses.textSecondary}>
                          Contact: {town.contactPerson}
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedTownId === town.id && (
                    <div className="ml-3">
                      <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {towns.length === 0 && (
            <div className="text-center py-8">
              <MapPin className={`h-12 w-12 ${darkModeClasses.textTertiary} mx-auto mb-3`} />
              <p className={`${darkModeClasses.textSecondary}`}>
                No towns available
              </p>
            </div>
          )}

          <div className="flex space-x-3 pt-6 mt-6 border-t border-gray-200 dark:border-dark-border-primary">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-hover-bg text-gray-800 dark:text-dark-text-primary py-2 px-4 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedTownId || loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <span>Access Member Portal</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};